import { getScrollParent } from '@/components/tiptap-editor/tiptap-extension/math-extension/utils';
import type { Editor } from '@tiptap/react';
import { useEffect, useState, type RefObject } from 'react';

type UseLazyMountWhenVisibleOptions = {
    containerRef: RefObject<Element | null>;
    editor: Editor;
    forceMount?: boolean;
};

/**
 * Latches to true once the container intersects the editor scroll area or forceMount is set.
 * Never resets to false — mount-only, no scroll tear-down.
 */
export function useLazyMountWhenVisible({ containerRef, editor, forceMount = false }: UseLazyMountWhenVisibleOptions): boolean {
    const [intersectionMounted, setIntersectionMounted] = useState(false);
    const [forceMountLatched, setForceMountLatched] = useState(forceMount);
    const [prevForceMount, setPrevForceMount] = useState(forceMount);

    if (forceMount !== prevForceMount) {
        setPrevForceMount(forceMount);
        if (forceMount) {
            setForceMountLatched(true);
        }
    }

    useEffect(() => {
        if (intersectionMounted || forceMountLatched) {
            return;
        }

        const element = containerRef.current;
        if (!element) {
            return;
        }

        let root: Element | undefined;
        try {
            root = getScrollParent(editor.view.dom) ?? undefined;
        } catch {
            // ignore
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                if (!entry?.isIntersecting) {
                    return;
                }
                observer.disconnect();
                setIntersectionMounted(true);
            },
            { root, rootMargin: '200px', threshold: 0 },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [containerRef, editor, forceMountLatched, intersectionMounted]);

    return intersectionMounted || forceMountLatched;
}
